import type {
  RewardsBreakdownQuery,
  RewardsBreakdownQuery$data,
} from '@/__generated__/RewardsBreakdownQuery.graphql'
import { ZORA_CLAIM_CONTRACT_ENV } from '@/constants/keys'
import * as Sentry from '@sentry/nextjs'
import { motion } from 'framer-motion'
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import { graphql, useLazyLoadQuery } from 'react-relay'
import { Button, Text, View } from 'reshaped'
import { RewardCard } from './RewardCard'
import styles from './RewardsBreakdown.module.css'

// Error logging function for Sentry
const logError = (error: Error) => {
  console.error('RewardsBreakdown error:', error)
  Sentry.captureException(error, {
    tags: {
      component: 'RewardsBreakdown',
    },
  })
}

type RewardCategory = keyof RewardsBreakdownQuery$data['zoraTokenAllocation']

interface RewardsBreakdownProps {
  walletAddresses?: string[]
  isSuccess?: boolean
}

const rewardsQuery = graphql`
  query RewardsBreakdownQuery(
    $walletAddresses: [String!]!
    $zoraClaimContractEnv: EZoraClaimContractEnv!
  ) {
    zoraTokenAllocation(
      identifierWalletAddresses: $walletAddresses
      zoraClaimContractEnv: $zoraClaimContractEnv
    ) {
      tokensEarnedMintReferrals {
        totalTokens
      }
      tokensEarnedCreatorReferrals {
        totalTokens
      }
      tokensEarnedPosts {
        totalTokens
      }
      tokensEarnedEthSpend {
        totalTokens
      }
      tokensEarnedRewards {
        totalTokens
      }
      tokensEarnedMintsOnPosts {
        totalTokens
      }
      tokensEarnedBonusTokensEarned {
        totalTokens
      }
    }
  }
`

const REWARD_DETAILS: Record<
  string,
  { title: string; description: string; imagePath: string }
> = {
  tokensEarnedMintReferrals: {
    title: 'Buy Referral',
    description: 'Users minted and traded through your activity, shared link or client',
    imagePath: '/assets/rewards/mint-referral.png',
  },
  tokensEarnedCreatorReferrals: {
    title: 'Create Referral',
    description: 'Users posted on Zora through your invite or client',
    imagePath: '/assets/rewards/create-referral.png',
  },
  tokensEarnedPosts: {
    title: 'Posts',
    description: 'Total posts you created on Zora',
    imagePath: '/assets/rewards/posts.png',
  },
  tokensEarnedEthSpend: {
    title: 'ETH Spent',
    description: 'ETH spent minting and trading on Zora',
    imagePath: '/assets/rewards/eth-spent.png',
  },
  tokensEarnedRewards: {
    title: 'ETH Earned',
    description: 'ETH earned from protocol rewards and NFT sales',
    imagePath: '/assets/rewards/rewards-earned.png',
  },
  tokensEarnedMintsOnPosts: {
    title: 'Mints and Trades',
    description: 'Mints and trades your posts received',
    imagePath: '/assets/rewards/mints.png',
  },
  tokensEarnedBonusTokensEarned: {
    title: 'OG User',
    description: 'You already know',
    imagePath: '/assets/rewards/og-user.png',
  },
}

// Define the cacheKey creator as a stable function
const createCacheKey = (walletAddresses: string[]) => {
  return walletAddresses.slice().sort().join(',')
}

// Cache for data to prevent requerying
const dataCache = new Map<string, any>()

export const RewardsBreakdown = memo(
  ({ walletAddresses = [], isSuccess = false }: RewardsBreakdownProps) => {
    const [showAllRewards, setShowAllRewards] = useState(false)

    // Use a stable cache key to avoid unnecessary refetches
    const cacheKey = useMemo(() => createCacheKey(walletAddresses), [walletAddresses])

    // Check if we have this data cached
    const cachedData = useMemo(() => dataCache.get(cacheKey), [cacheKey])

    // Only fetch if we don't have cached data
    const data = useLazyLoadQuery<RewardsBreakdownQuery>(
      rewardsQuery,
      {
        walletAddresses,
        zoraClaimContractEnv: ZORA_CLAIM_CONTRACT_ENV,
      },
      {
        fetchPolicy: cachedData ? 'store-only' : 'store-or-network',
        fetchKey: cacheKey,
      }
    )

    useEffect(() => {
      if (data && !cachedData) {
        dataCache.set(cacheKey, data)
      }
    }, [data, cacheKey, cachedData])

    // Memoize the rewards calculation
    const rewards = useMemo(() => {
      if (!walletAddresses.length || !data?.zoraTokenAllocation) return []

      const allocationEntries = Object.entries(data.zoraTokenAllocation ?? {})

      return allocationEntries
        .filter(([id, alloc]) => {
          return (
            alloc &&
            typeof alloc === 'object' &&
            'totalTokens' in alloc &&
            REWARD_DETAILS[id]
          )
        })
        .map(([id, alloc]) => {
          const allocation = alloc as { totalTokens: number }
          const amount = allocation.totalTokens

          return {
            id: id as RewardCategory,
            title: REWARD_DETAILS[id].title,
            description: REWARD_DETAILS[id].description,
            imagePath: REWARD_DETAILS[id].imagePath,
            amount,
          }
        })
    }, [data?.zoraTokenAllocation, walletAddresses])

    // Separate rewards into active (amount > 0) and inactive (amount === 0)
    const { activeRewards, inactiveRewards } = useMemo(() => {
      const active = rewards.filter((reward) => reward.amount > 0)
      const inactive = rewards.filter((reward) => reward.amount === 0)

      return {
        activeRewards: active,
        inactiveRewards: inactive,
      }
    }, [rewards])

    // Create a combined list for rendering in a single grid
    const allRewards = useMemo(() => {
      return [...activeRewards, ...inactiveRewards]
    }, [activeRewards, inactiveRewards])

    const toggleShowAllRewards = useCallback(() => {
      setShowAllRewards((prev) => !prev)
    }, [])

    return (
      <View
        width="100%"
        gap={4}
        paddingTop={6}
        paddingBottom={12}
        padding={{ m: !isSuccess ? 6 : 4 }}
        zIndex={30}
        position="relative"
        backgroundColor="page"
      >
        {isSuccess && (
          <View
            attributes={{
              style: {
                position: 'absolute',
                top: '0',
                left: '50%',
                transform: 'translateX(-50%)',
                height: '100%',
                width: '100vw',
                background: '#FFFFFF',
                boxShadow: '0px -120px 60px 40px #FFFFFF',
                zIndex: -1,
              },
            }}
          />
        )}
        <View
          gap={8}
          paddingInline={{ s: 0, m: 6 }}
          width={{ s: '100%', l: '860px' }}
          maxWidth={{ s: '400px', l: '860px' }}
          zIndex={1}
          attributes={{
            style: {
              margin: '0 auto',
            },
          }}
        >
          <View>
            <Text variant="featured-2" weight="semibold" color="primary" align="center">
              Rewards breakdown
            </Text>
            <Text variant="body-2" weight="semibold" color="neutral-faded" align="center">
              The actions that made you eligible for this airdrop
            </Text>
          </View>

          {/* All rewards in a single grid */}
          <View
            direction="row"
            wrap
            gap={6}
            justify="center"
            paddingBottom={inactiveRewards.length > 0 && !showAllRewards ? 4 : 8}
          >
            {allRewards.length > 0 &&
              allRewards.map((reward) => {
                const isInactive = reward.amount === 0

                // Only show inactive rewards if showAllRewards is true
                if (isInactive && !showAllRewards) {
                  return null
                }

                return (
                  <View.Item key={reward.id} columns={{ s: 6, m: 6, l: 3 }}>
                    {isInactive ? (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.5 }}
                      >
                        <RewardCard reward={reward} inactive={true} />
                      </motion.div>
                    ) : (
                      <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                      >
                        <RewardCard reward={reward} />
                      </motion.div>
                    )}
                  </View.Item>
                )
              })}
          </View>
          {inactiveRewards.length > 0 && !showAllRewards && (
            <View align="center" paddingBottom={8}>
              <Button onClick={toggleShowAllRewards} className={styles.viewAllButton}>
                <Text variant="body-1" weight="semibold" color="neutral">
                  View all
                </Text>
              </Button>
            </View>
          )}
        </View>
      </View>
    )
  }
)

// Create a wrapped version of the component that includes the error boundary
export const RewardsBreakdownWithErrorBoundary = (props: RewardsBreakdownProps) => (
  <ErrorBoundary fallbackRender={() => null} onError={logError}>
    <RewardsBreakdown {...props} />
  </ErrorBoundary>
)

// Export the wrapped version as default for easier imports
export default RewardsBreakdownWithErrorBoundary
